import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaSearch, FaTools, FaCubes, FaMoneyBillWave, FaFileAlt, FaExchangeAlt } from 'react-icons/fa';
import CatalogService, { CatalogItem, CatalogItemKind } from '../services/CatalogService';

interface CatalogItemPickerProps {
  onSelect: (item: CatalogItem) => void;
  kindFilter?: CatalogItemKind;
  placeholder?: string;
  disabled?: boolean;
  compact?: boolean;
}

export default function CatalogItemPicker({
  onSelect,
  kindFilter,
  placeholder = 'Search items...',
  disabled = false,
  compact = false,
}: CatalogItemPickerProps): React.JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const performSearch = useCallback(async (term: string, showAll: boolean = false) => {
    setLoading(true);
    try {
      const response = await CatalogService.search({
        search: term.trim() || undefined,
        kind: kindFilter,
        page_size: showAll ? 50 : 10,
        ordering: 'name',
      });
      setResults(response.results);
      setShowDropdown(true);
      setHighlightedIndex(-1);
    } catch (err) {
      console.error('Catalog search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [kindFilter]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (showDropdown) {
      debounceRef.current = setTimeout(() => {
        performSearch(searchTerm, !searchTerm.trim());
      }, 300);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, showDropdown, performSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current && containerRef.current.contains(target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(target);

      if (!isInsideContainer && !isInsideDropdown) {
        setShowDropdown(false);
        if (compact) {
          setIsExpanded(false);
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [compact]);

  // Update dropdown position when shown
  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (showDropdown) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [showDropdown, updateDropdownPosition]);

  const handleSelect = (item: CatalogItem) => {
    onSelect(item);
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    if (compact) {
      setIsExpanded(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const getKindIcon = (kind: CatalogItemKind): React.ReactNode => {
    switch (kind) {
      case 'SERVICE':
        return <FaTools size={12} />;
      case 'MATERIAL':
        return <FaCubes size={12} />;
      case 'FLAT_FEE':
        return <FaMoneyBillWave size={12} />;
      case 'TEXT':
        return <FaFileAlt size={12} />;
      default:
        return <FaTools size={12} />;
    }
  };

  const getKindColor = (kind: CatalogItemKind): { bg: string; text: string } => {
    switch (kind) {
      case 'SERVICE':
        return { bg: '#dbeafe', text: '#1d4ed8' };
      case 'MATERIAL':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'FLAT_FEE':
        return { bg: '#dcfce7', text: '#166534' };
      case 'TEXT':
        return { bg: '#f3e8ff', text: '#7c3aed' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  const handleExpandClick = () => {
    if (disabled) return;
    setIsExpanded(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: compact && !isExpanded ? 'auto' : '100%' }}>
      {/* Compact mode: show icon button when collapsed */}
      {compact && !isExpanded ? (
        <button
          onClick={handleExpandClick}
          disabled={disabled}
          title="Artikel ändern"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px 8px',
            background: 'none',
            border: '1px solid #e0e3ef',
            borderRadius: '4px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: disabled ? '#ccc' : '#666',
            fontSize: '12px',
            gap: '4px',
          }}
        >
          <FaExchangeAlt size={10} />
        </button>
      ) : (
        <div style={{ position: 'relative' }}>
          <FaSearch
            size={14}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888',
            }}
          />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => {
              setShowDropdown(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            style={{
              width: '100%',
              padding: compact ? '6px 10px 6px 32px' : '10px 12px 10px 36px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              fontSize: compact ? '12px' : '14px',
              outline: 'none',
              background: disabled ? '#f5f5f5' : 'white',
              cursor: disabled ? 'not-allowed' : 'text',
            }}
          />
          {loading && (
            <span
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '12px',
                color: '#888',
              }}
          >
            ...
          </span>
        )}
        </div>
      )}

      {showDropdown && results.length > 0 && dropdownPosition && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPosition.top - window.scrollY,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            minWidth: '320px',
            background: 'white',
            border: '1px solid #e0e3ef',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            maxHeight: '300px',
            overflow: 'auto',
          }}
        >
          {results.map((item, index) => {
            const kindColor = getKindColor(item.kind);
            const isHighlighted = index === highlightedIndex;

            return (
              <div
                key={item.id}
                onClick={() => handleSelect(item)}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderBottom: index < results.length - 1 ? '1px solid #f0f0f0' : 'none',
                  background: isHighlighted ? '#f8f9fc' : 'white',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600, color: '#021a4a', fontSize: '14px' }}>
                        {item.name}
                      </span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: kindColor.bg,
                          color: kindColor.text,
                        }}
                      >
                        {getKindIcon(item.kind)}
                        {CatalogService.getKindLabel(item.kind)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#666' }}>
                      <span>Art.-Nr.: {item.item_number}</span>
                      <span>Einheit: {item.unit}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, color: '#021a4a', fontSize: '14px' }}>
                      {CatalogService.formatPrice(item.sales_price)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                      pro {item.unit}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>,
        document.body
      )}

      {showDropdown && searchTerm.trim() && results.length === 0 && !loading && dropdownPosition && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: dropdownPosition.top - window.scrollY,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            minWidth: '320px',
            padding: '16px',
            background: 'white',
            border: '1px solid #e0e3ef',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            textAlign: 'center',
            color: '#888',
            fontSize: '14px',
          }}
        >
          No items found
        </div>,
        document.body
      )}
    </div>
  );
}
